<template>
  <div data-component-group="business-form" data-component="cpm">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <magic-container>
        <v-autocomplete
          v-model="country"
          :items="enabledPromotionsCountries"
          prepend-inner-icon="public"
          :label="$t('country')"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :error-messages="countryErrors"
          @blur="v$.country.$touch()"
        />
        <v-autocomplete
          v-model="advertiser"
          :items="businessAdvertisers"
          :label="$t('advertiser')"
          item-title="name"
          item-value="id"
          :return-object="true"
          :readonly="isDisabled"
          :disabled="!hasEnabledPromotionsCountries"
          required
          :error-messages="advertiserErrors"
          @blur="v$.advertiser.$touch()"
        />
        <v-autocomplete
          v-model="billingAccount"
          :items="accounts"
          :loading="isLoadingBillingAccounts"
          prepend-inner-icon="credit_card"
          :label="$t('billing_account')"
          item-title="legal_business_name"
          item-value="id"
          :return-object="true"
          required
          :readonly="isDisabled"
          :disabled="!hasEnabledPromotionsCountries"
          :error-messages="billingAccountErrors"
          v-model:search="billingAccountSearch"
          @blur="v$.billingAccount.$touch()"
        />
      </magic-container>

      <div class="component-actions">
        <Can action="update" subject="business">
          <v-btn
            color="secondary"
            :loading="isLoading"
            :disabled="!hasEnabledPromotionsCountries || !canSubmitRequest()"
            @click="onUpdate"
          >
            <v-icon>edit</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </Can>
      </div>
    </div>
    <div class="component-body">
      <div class="item-grid-rows" :class="{ 'item-grid-rows--edit': !isRemovableDisabled }">
      <Removables
        v-if="hasItems"
        :items="billingAccountAdvertisers"
        :is-disabled="isRemovableDisabled"
        :on-remove="onRemoveBillingAccount"
      >
        <template v-slot:item="scope" >
          <div class="item-grid">
            <span class="d-flex align-center">
              <v-icon>public</v-icon>
              <span>{{ scope.item.country.name }}</span>
            </span>

            <span>
              {{ scope.item.advertiser.name }}
            </span>

            <span>
              <router-link :to="{ name: 'billing-edit', params: { id: scope.item.account.id } }" target="_blank">{{ scope.item.account.legal_business_name }}</router-link>
            </span>
          </div>
        </template>
      </Removables>
      </div>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import Removables from '../../lists/Removables';
  import { isObj, isStr, isNum, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
        advertiser: {
          required,
        },
        billingAccount: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Add',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        advertiser: null,
        billingAccount: null,
        accounts: [],
        billingAccountSearch: '',
        isRefreshingAdvertiser: false,
        isLoading: false,
        isLoadingBillingAccounts: false,
        isRefreshingBillingAccounts: false,
      };
    },
    computed: {
      activeBusiness() {
        return this.$store.getters['businesses/activeItem'] || {};
      },
      userCountries() {
        return this.$store.getters['users/countries'] || [];
      },
      enabledPromotionsCountries() {
        const enabledPromotions = this.activeBusiness?.enabledPromotions ?? [];

        return this.userCountries.filter((item) => {
          return enabledPromotions.find(promotionCountry => item.code === promotionCountry.country.code);
        }) || [];
      },
      businessAdvertisers() {
        return this.activeBusiness?.businessAdvertisers || [];
      },
      billingAccountAdvertisers() {
        return this.activeBusiness?.billingAccountAdvertisers || [];
      },
      hasItems() {
        return this.businessAdvertisers.length > 0;
      },
      hasEnabledPromotionsCountries() {
        return this.enabledPromotionsCountries.length > 0;
      },
      canUpdateBusiness() {
        return this.$ability.can('update', 'business');
      },
      canUnassignBillingFromAdvertiser() {
        return this.$ability.can('unassign-advertiser-billing', 'business');
      },
      isRemovableDisabled() {
        return this.isDisabled || !this.canUnassignBillingFromAdvertiser;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('country_is_required');
          }
          return error.$message;
        });
      },
      advertiserErrors() {
        return this.v$.advertiser.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('advertiser_is_required');
          }
          return error.$message;
        });
      },
      billingAccountErrors() {
        return this.v$.billingAccount.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('billing_account_is_required');
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getBillingAccountAdvertisers(this.business);
          }
        }
      },
      advertiser() {
        this.isRefreshingAdvertiser = true;
      },
      billingAccount() {
        this.isRefreshingBillingAccounts = true;
      },
      billingAccountSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBillingAccounts) {
            this.isRefreshingBillingAccounts = false;
            return false;
          }

          debouncer(() => this.getAccounts(nextSearch));
        }
      },
    },
    methods: {
      getBusinessAdvertisers() {
        const { id } = this.business;
        const query = {
          id,
        };

        return this.$store.dispatch('businesses/getBusinessAdvertisers', query);
      },
      getAccounts(search = '') {
        const query = {
          per_page: 15,
          search,
        };

        this.isLoadingBillingAccounts = true;
        return this.$store
          .dispatch('billing/getItems', query)
          .then((res) => {
            this.isLoadingBillingAccounts = false;
            this.accounts = res.items;
            return res;
          })
          .catch((err) => {
            this.isLoadingBillingAccounts = false;
            return err;
          });
      },
      getBillingAccountAdvertisers({ id }) {
        const query = { id };
        return this.$store.dispatch(
          'businesses/getBillingAccountAdvertisers',
          query,
        );
      },
      getEnabledPromotionsCountries({ id }) {
        const query = {
          id,
          only_allowed_countries: true,
        };
        return this.$store.dispatch('businesses/getEnabledPromotions', query);
      },
      canSubmitRequest() {
        return (
          isObj(this.country) &&
          isObj(this.country) &&
          isStr(this.country.code) &&
          isNum(this.business.id) &&
          isNum(this.advertiser.id) &&
          isNum(this.billingAccount.id)
        );
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }
        this.isLoading = true;
        const { business, country, advertiser, billingAccount } = this;
        const query = {
          id: business.id,
          country: country.code,
          advertiser,
          account: billingAccount,
        };

        this.onSubmit(query)
          .then((res) => {
            this.clear();
            return res;
          })
          .catch((err) => err)
          .finally(()=>{
            this.isLoading = false;
          });
      },
      onRemoveBillingAccount(item) {
        const query = {
          id: this.business.id,
          account: item,
        };

        return this.$store.dispatch(
          'businesses/removeBillingAccountAdvertiser',
          query,
        );
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.advertiser = null;
        this.billingAccount = null;
        this.accounts = [];
        this.billingAccountSearch = '';
        this.isRefreshingAdvertiser = false;
        this.isLoadingBillingAccounts = false;
        this.isRefreshingBillingAccounts = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='cpm'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    padding: 8px 0 16px 0;
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      // height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
